import React, { useState, useEffect } from 'react';
import createForm from './createLeboncoinForm';

type FormValues = Record<string, any>;

type VisibilityRule = {
  previous_associated_key?: string;
  previous_raw_answer?: string[];
  operator?: string;
};

function checkVisibility(rules: VisibilityRule[], formValues: FormValues): boolean {
  if (!rules || rules.length === 0) return true;

  return rules.every(rule => {
    if (!rule.previous_associated_key) return true;

    const currentValue = formValues[rule.previous_associated_key];
    
    switch (rule.operator) {
      case 'REQUIREMENT_OPERATOR_EQUAL':
        return rule.previous_raw_answer?.includes(currentValue);
      
      case 'REQUIREMENT_OPERATOR_IS_ANSWERED':
        return currentValue !== undefined && currentValue !== '';
      
      case 'REQUIREMENT_OPERATOR_IS_NOT_ANSWERED':
        return currentValue === undefined || currentValue === '';
      
      case 'REQUIREMENT_OPERATOR_INFERIOR_STRICT':
        return Number(currentValue) < Number(rule.previous_raw_answer?.[0]);
      
      default:
        return true;
    }
  });
}

export function LeboncoinAttributesForm({
  fieldsToDisplay,
  fieldsDefinition,
  data,
  onChange
}: { fieldsToDisplay: string[], fieldsDefinition: any, data: any, onChange: (values: FormValues) => void }) {
  const [form, setForm] = useState<any>(null);
  useEffect(() => {
    setForm(createForm(
      fieldsToDisplay,
      fieldsDefinition
    ));
  }, [fieldsToDisplay, fieldsDefinition]);

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      [fieldId]: value
    });
  };
  
  if (!form) return null;

  return (
    <div className="space-y-6">
      {form.map((field) => {
        if (!field) return null;
        
        // Check visibility for all field types
        const visibilityRules = 'type' in field ? field.visibilityRules : field.props?.visibilityRules;
        if (!checkVisibility(visibilityRules || [], data)) {
          return null;
        }

        // Handle static content
        if (field.type === 'static') {
          return (
            <div key={field.identifier} className="helper-text">
              {field.content}
            </div>
          );
        }
        
        // Handle components (Select, Toggle, TextInput)
        if ('Component' in field) {
          const { Component, props } = field;
          const { visibilityRules, ...componentProps } = props;
          
          return (
            <Component
              key={props.value_key}
              {...componentProps}
              value={data[props.value_key]}
              onChange={(value: any) => handleFieldChange(props.value_key, value)}
              formData={data}
            />
          );
        }
        
        return null;
      })}
    </div>
  )
} 

// Chinese to French men shoe size mapping
export const shoeSizeMen_CN_to_FR_mapping = {
  '36': {
    fullLabel: '36,5 (23cm)',
    shortLabel: '36,5',
    cms: 23,
  },
  '37': {
    fullLabel: '37 (23,5cm)',
    shortLabel: '37',
    cms: 23.5,
  },
  '38': {
    fullLabel: '38 (24cm)',
    shortLabel: '38',
    cms: 24,
  },
  '39': {
    fullLabel: '38,5 (24,5cm)',
    shortLabel: '38,5',
    cms: 24.5,
  },
  '40': {
    fullLabel: '39 (25cm)',
    shortLabel: '39',
    cms: 25,
  },
  '41': {
    fullLabel: '40 (25,5cm)',
    shortLabel: '40',
    cms: 25.5,
  },
  '42': {
    fullLabel: '41 (26cm)',
    shortLabel: '41',
    cms: 26,
  },
  '43': {
    fullLabel: '42 (26,5cm)',
    shortLabel: '42',
    cms: 26.5,
  },
  '44': {
    fullLabel: '42,5 (27cm)',
    shortLabel: '42,5',
    cms: 27,
  },
  '45': {
    fullLabel: '43,5 (27,5cm)',
    shortLabel: '43,5',
    cms: 27.5,
  },
  '46': {
    fullLabel: '44 (28cm)',
    shortLabel: '44',
    cms: 28,
  },
  '47': {
    fullLabel: '44,5 (28,5cm)',
    shortLabel: '44,5',
    cms: 28.5,
  },
  '48': {
    fullLabel: '45.5 (29cm)',
    shortLabel: '45.5',
    cms: 29,
  },
  '49': {
    fullLabel: '46 (29,5cm)',
    shortLabel: '46',
    cms: 29.5,
  },
  '50': {
    fullLabel: '47 (30cm)',
    shortLabel: '47',
    cms: 30,
  },
}


export const LBC_shoeSizes = [
  { identifier: "1", label: "16" },
  { identifier: "36", label: "16,5" },
  { identifier: "2", label: "17" },
  { identifier: "37", label: "17,5" },
  { identifier: "3", label: "18" },
  { identifier: "38", label: "18,5" },
  { identifier: "4", label: "19" },
  { identifier: "39", label: "19,5" },
  { identifier: "5", label: "20" },
  { identifier: "40", label: "20,5" },
  { identifier: "6", label: "21" },
  { identifier: "41", label: "21,5" },
  { identifier: "7", label: "22" },
  { identifier: "42", label: "22,5" },
  { identifier: "8", label: "23" },
  { identifier: "43", label: "23,5" },
  { identifier: "9", label: "24" },
  { identifier: "44", label: "24,5" },
  { identifier: "10", label: "25" },
  { identifier: "45", label: "25,5" },
  { identifier: "11", label: "26" },
  { identifier: "46", label: "26,5" },
  { identifier: "12", label: "27" },
  { identifier: "47", label: "27,5" },
  { identifier: "13", label: "28" },
  { identifier: "48", label: "28,5" },
  { identifier: "14", label: "29" },
  { identifier: "49", label: "29,5" },
  { identifier: "15", label: "30" },
  { identifier: "50", label: "30,5" },
  { identifier: "16", label: "31" },
  { identifier: "51", label: "31,5" },
  { identifier: "17", label: "32" },
  { identifier: "52", label: "32,5" },
  { identifier: "18", label: "33" },
  { identifier: "53", label: "33,5" },
  { identifier: "19", label: "34" },
  { identifier: "54", label: "34,5" },
  { identifier: "20", label: "35" },
  { identifier: "55", label: "35,5" },
  { identifier: "21", label: "36" },
  { identifier: "56", label: "36,5" },
  { identifier: "22", label: "37" },
  { identifier: "57", label: "37,5" },
  { identifier: "23", label: "38" },
  { identifier: "58", label: "38,5" },
  { identifier: "24", label: "39" },
  { identifier: "59", label: "39,5" },
  { identifier: "25", label: "40" },
  { identifier: "60", label: "40,5" },
  { identifier: "26", label: "41" },
  { identifier: "61", label: "41,5" },
  { identifier: "27", label: "42" },
  { identifier: "62", label: "42,5" },
  { identifier: "28", label: "43" },
  { identifier: "63", label: "43,5" },
  { identifier: "29", label: "44" },
  { identifier: "64", label: "44,5" },
  { identifier: "30", label: "45" },
  { identifier: "65", label: "45,5" },
  { identifier: "31", label: "46" },
  { identifier: "66", label: "46,5" },
  { identifier: "32", label: "47" },
  { identifier: "67", label: "47,5" },
  { identifier: "33", label: "48" },
  { identifier: "68", label: "48,5" },
  { identifier: "34", label: "49" },
  { identifier: "69", label: "49,5" },
  { identifier: "35", label: "50 et plus" }
];
